import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import styles from "./FleetMonitoring.module.scss";

import {PageContainer} from "@renta-apps/athenaeum-react-components";
import AlertsGrid, {EnabledAlertGridModel} from "@/pages/FleetMonitoring/AlertsGrid/AlertsGrid";
import {IPagedList, SortDirection} from "@renta-apps/athenaeum-toolkit";
import {EnabledAlertModel} from "@/models/server/EnabledAlertModel";
import {getEnabledAlertsPagedListAsync} from "@/services/FleetService";
import {SubscriptionAlertConfig} from "@/components/AlertSubscriptionsManagement/AlertSubscriptionsManagement";
import {CHECKBOX_PARTIAL, IDropdownItem, Pagination} from "@renta-apps/renta-react-components";
import RequestReturnModal from "@/pages/FleetMonitoring/Modals/RequestReturnModal/RequestReturnModal";
import {ch} from "@renta-apps/athenaeum-react-common";
import UserContext from "@/models/server/UserContext";
import BaseAlarmModel from "@/models/BaseAlarmModel";
import SubscribeToAlertsModal from "@/pages/FleetMonitoring/Modals/SubscribeToAlertsModal";
import {IDeletedAlarm, INewAlarm} from "@/pages/FleetMonitoring/Devices";
import {AlarmTypeHelper} from "@/helpers/AlarmTypeHelper";
import {FleetMonitoringFiltersAndPagination} from "@/pages/FleetMonitoring/FleetMonitoring";
import ViewControl, {FleetMonitoringFiltersSortAndPagination} from "@/pages/FleetMonitoring/ViewControl/ViewControl";
import UserInteractionDataStorage, {DataStorageType} from "@/providers/UserInteractionDataStorage";
import {useFiltersState} from "@/pages/FleetMonitoring/Hooks/useFiltersState";
import Localizer from "@/localization/Localizer";
import {useDebounceLoad} from "@/pages/FleetMonitoring/Hooks/useDebounceLoad";
import {getContractData, getContractDataByOrganizationId} from "@/services/CompanyService";
import {OrganizationContractModel} from "@/models/server/OrganizationContractModel";

interface IAlertSourcesProps {
    urlParams?: FleetMonitoringFiltersAndPagination;
    userRoleConstructionSiteId: string | null;
    userRoleContractId: string | null;
    userRoleOrganizationId: string | null;
    userRoleIsAdmin: boolean;
    isSubscribeToAlertsModalOpen: boolean;
    isReturnRequestModalOpen: boolean;
    onFilterParamsChange: (filters: FleetMonitoringFiltersAndPagination) => void;
    onDeviceBottomSheetVisibilityChange: (isVisible: boolean) => void;
    onSubscribeToAlertsModalVisibilityChange: (isVisible: boolean) => void;
    onReturnRequestModalVisibilityChange: (isVisible: boolean) => void;
    onReturnRequestBottomButtonEnableChange: (isEnabled: boolean) => void;
}

const mapToEnabledAlertGridModel = (enabledAlert: EnabledAlertModel): EnabledAlertGridModel => {
    return {
        ...enabledAlert,
        alertsConfig: AlarmTypeHelper.selectedDevicesConfig.map(config => {
            const alert = config.getConfiguredAlert(enabledAlert);

            return {
                type: config.type,
                supported: config.supported(enabledAlert),
                enabled: !!alert,
                alarmData: alert?.alarmData ?? undefined,
            };
        }),
    };
};

const defaultPageNumber = 1;
const defaultPageSize = 25;

const AlertSources: React.FC<IAlertSourcesProps> = (props: IAlertSourcesProps) => {
    const USER_DATA_STORAGE_KEY = useMemo(() => {
        return `EnabledAlertsFilters-${props.userRoleOrganizationId}-${props.userRoleContractId}-${props.userRoleConstructionSiteId}`;
    }, []);

    const createInitialFilters = useCallback((): FleetMonitoringFiltersSortAndPagination => {
        let initialParams = props.urlParams;
        if (!initialParams) {
            initialParams = UserInteractionDataStorage.get(USER_DATA_STORAGE_KEY, new FleetMonitoringFiltersAndPagination(), DataStorageType.Page);
        }

        return {
            companies: initialParams!.companies || [],
            constructionSites: initialParams!.constructionSites || [],
            depots: initialParams!.depots || [],
            deviceNames: initialParams!.deviceNames || [],
            productGroups: initialParams!.productGroups || [],
            sortBy: initialParams!.sortBy ?? 'LastOccurence',
            sortOrder: initialParams!.sortOrder ?? 'Desc',
            statuses: initialParams!.statuses || [],
            pageNumber: initialParams!.pageNumber === undefined ? defaultPageNumber : Number(initialParams!.pageNumber),
            pageSize: initialParams!.pageSize === undefined ? defaultPageSize : Number(initialParams!.pageSize),
        };
    }, []);

    const [
        filtersSortAndPagination,
        setFilterFromDropdownItems,
        setFiltersPartial,
        resetFilters,
    ] = useFiltersState(createInitialFilters);

    const [enabledAlerts, setEnabledAlerts] = useState<EnabledAlertGridModel[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [selectedDevicesKeys, setSelectedDevicesKeys] = useState<Set<string>>(new Set());
    const [selectedDevices, setSelectedDevices] = useState<EnabledAlertGridModel[]>([]);
    const [totalItemCount, setTotalItemCount] = useState(0);
    const [scrollToTop, setScrollToTop] = useState(false);
    const [initialLoad, setInitialLoad] = useState(true);
    const [userCompaniesList, setUserCompaniesList] = useState<IDropdownItem[]>([]);

    useEffect(() => {
        loadContractDataList(props.userRoleOrganizationId, props.userRoleContractId, props.userRoleIsAdmin).catch();
    }, []);

    const dataGridRef = useRef<HTMLDivElement>(null);
    const currentRequestIdRef = useRef<string | null>(null);

    const sortByItems: IDropdownItem[] = [
        {
            name: Localizer.fleetMonitoringPageFiltersSortByLatestAlertTime,
            value: "LastOccurence",
        },
        {
            name: Localizer.fleetMonitoringPageFiltersSortByName,
            value: "DeviceName",
        },
        {
            name: Localizer.fleetMonitoringPageFiltersSortByDepot,
            value: "DepotName",
        },
        {
            name: Localizer.fleetMonitoringPageGridId,
            value: "RentaId",
        },
        {
            name: Localizer.fleetMonitoringPageFiltersSortByConstructionSite,
            value: "ConstructionSiteName",
        },
        {
            name: Localizer.fleetMonitoringPageGridProductGroup,
            value: "ProductGroupName",
        },
    ];

    const userContext = ch.getContext() as UserContext;

    const handleOnPageNumberChange = (pageNumber: number) => {
        setScrollToTop(true);
        setFiltersPartial({pageNumber});
    };

    const handleOnPageSizeChange = (pageSize: number) => {
        setScrollToTop(true);
        setFiltersPartial({pageSize});
    };

    const saveUserSelection = (filters: FleetMonitoringFiltersSortAndPagination) => {
        props.onFilterParamsChange(filters);

        UserInteractionDataStorage.set(USER_DATA_STORAGE_KEY, filters, DataStorageType.Page);
    };

    const loadAlertSources = async (filters: FleetMonitoringFiltersSortAndPagination, requestId: string) => {
        setIsLoading(true);

        try {
            const sortDirectionEnum = SortDirection[filters.sortOrder as keyof typeof SortDirection];

            const data: IPagedList<EnabledAlertModel> = await getEnabledAlertsPagedListAsync(
                filters.pageNumber,
                filters.pageSize,
                props.userRoleOrganizationId ?? undefined,
                props.userRoleContractId ?? undefined,
                (props.userRoleIsAdmin || !props.userRoleConstructionSiteId) ? undefined : props.userRoleConstructionSiteId,
                filters.sortBy!,
                sortDirectionEnum,
                filters.deviceNames,
                filters.companies,
                filters.constructionSites,
                filters.depots,
                filters.productGroups,
                filters.statuses,
            );

            if (currentRequestIdRef.current !== requestId) {
                return;
            }

            setEnabledAlerts(data.items.map(mapToEnabledAlertGridModel));
            setSelectedDevicesKeys(new Set());
            setTotalItemCount(data.totalItemCount);
        } catch (error) {
            console.error('Error loading alert sources: ', error);
        } finally {
            setIsLoading(false);
            setInitialLoad(false);
            if (scrollToTop && dataGridRef.current) {
                setScrollToTop(false);
                // 45 - height of the top nav bar
                window.scrollTo({top: dataGridRef.current.offsetTop - 45, behavior: 'smooth'});
            }
        }
    };

    const loadContractDataList = async (userRoleOrganizationId: string | null, userRoleContractId: string | null, userRoleIsAdmin: boolean) => {
        if (userRoleIsAdmin || (!userRoleOrganizationId && !userRoleContractId)) {
            setUserCompaniesList([]);
            return;
        }

        try {
            let companiesList: IDropdownItem[] = [];

            if (userRoleOrganizationId) {
                const organizationData = await getContractDataByOrganizationId(userRoleOrganizationId);
                companiesList = organizationData.organizationContracts.map(mapCompanyToDropdownItem);
            } else if (userRoleContractId) {
                const contractData = await getContractData(userRoleContractId);
                const parent = mapCompanyToDropdownItem(contractData.organizationContract!);
                const children = contractData.organizationContract?.children?.map(mapCompanyToDropdownItem) ?? [];
                companiesList = [parent, ...children];
            }

            setUserCompaniesList(companiesList);
        } catch (error) {
            console.error('Error loading contract data:', error);
        }
    };

    const mapCompanyToDropdownItem = (company: OrganizationContractModel): IDropdownItem => {
        return {
            name: `${company.name ?? ''} ${company.customerNumber ? `(${company.customerNumber})` : ''}`,
            value: company.contractId,
        };
    };

    const updateDevicesSubscriptions = (newAlarms: INewAlarm[], deletedAlarms: IDeletedAlarm[], alertsConfig: SubscriptionAlertConfig[]) => {
        selectedDevices.forEach(device => {
            const newAlerts = newAlarms.filter(x => x.deviceId === device.rentaId).map(x => x.configuredAlert);
            const removedAlertIds = deletedAlarms.filter(x => x.deviceId === device.rentaId).map(x => x.alarmId);
            updateDeviceSubscriptions(device.rentaId, alertsConfig, newAlerts, removedAlertIds);
        });
    };

    const updateDeviceSubscriptions = (rentaId: string, alertsConfig: SubscriptionAlertConfig[], newAlerts: BaseAlarmModel[], removedAlertIds: string[]) => {
        setEnabledAlerts(prevEnabledAlerts => prevEnabledAlerts.map(enabledAlert => {
            if (enabledAlert.rentaId !== rentaId) {
                return enabledAlert;
            }

            return {
                ...enabledAlert,
                configuredAlerts: enabledAlert.configuredAlerts.filter(x => !removedAlertIds.includes(x.id)).concat(newAlerts),
                alertsConfig: enabledAlert.alertsConfig.map(config => {
                    if (!config.supported) {
                        return config;
                    }

                    const newConfig = alertsConfig.find(x => x.type === config.type)!;
                    const newAlarmData = JSON.stringify(newConfig.alarmData);
                    const currentAlarmData = JSON.stringify(config.alarmData);
                    if (newConfig.enabled === CHECKBOX_PARTIAL || (newConfig.enabled === config.enabled && newAlarmData === currentAlarmData)) {
                        return config;
                    }

                    return {
                        ...config,
                        enabled: newConfig.enabled,
                        alarmData: newConfig.alarmData,
                    };
                }),
            };
        }));
    };

    useEffect(() => {
        const selectedDevices = enabledAlerts.filter(alert => selectedDevicesKeys.has(alert.rentaId));
        setSelectedDevices(selectedDevices);
        const constructionSiteIdsSet = new Set(selectedDevices.map(device => device.constructionSiteId));
        props.onReturnRequestBottomButtonEnableChange(constructionSiteIdsSet.size === 1 && !constructionSiteIdsSet.has(null));
        props.onDeviceBottomSheetVisibilityChange(selectedDevices.length > 0);
    }, [selectedDevicesKeys, enabledAlerts]);

    const handleOnItemClick = (selectedDevices: string[]): void => {
        setSelectedDevicesKeys(new Set(selectedDevices));
    };

    useDebounceLoad(filtersSortAndPagination, currentRequestIdRef, {
        saveUserSelection,
        loadData: loadAlertSources,
        initialLoad,
    });

    return (
        <PageContainer className={styles.pageContainer}>
            <div className={styles.devices}>
                <div id="container" className={styles.container}>
                    <ViewControl userRoleConstructionSiteId={props.userRoleConstructionSiteId}
                                 userRoleContractId={props.userRoleContractId}
                                 userRoleOrganizationId={props.userRoleOrganizationId}
                                 userRoleIsAdmin={props.userRoleIsAdmin}
                                 filters={filtersSortAndPagination}
                                 sortByItems={sortByItems}
                                 userCompaniesList={userCompaniesList}
                                 mode="alerts"
                                 onDropdownFieldChanged={setFilterFromDropdownItems}
                                 onFiltersChanged={setFiltersPartial}
                                 onFiltersReset={resetFilters}
                    />

                    <AlertsGrid isLoading={isLoading}
                                data={enabledAlerts}
                                selectedItemKeys={selectedDevicesKeys}
                                organizationId={props.userRoleOrganizationId}
                                contractId={props.userRoleContractId}
                                constructionSiteId={props.userRoleConstructionSiteId}
                                onRowCheckboxClick={handleOnItemClick}
                                onDeviceSubscriptionsChange={updateDeviceSubscriptions}
                                gridRef={dataGridRef}
                    />

                    <Pagination pageNumber={filtersSortAndPagination.pageNumber}
                                pageSize={filtersSortAndPagination.pageSize}
                                totalItemCount={totalItemCount}
                                onPageNumberChange={handleOnPageNumberChange}
                                onPageSizeChange={handleOnPageSizeChange}
                    />
                </div>
            </div>

            <SubscribeToAlertsModal selectedDevices={selectedDevices}
                                    updateDevicesSubscriptions={updateDevicesSubscriptions}
                                    isOpen={props.isSubscribeToAlertsModalOpen}
                                    onClose={() => props.onSubscribeToAlertsModalVisibilityChange(false)}
            />

            <RequestReturnModal isOpen={props.isReturnRequestModalOpen}
                                onClose={() => props.onReturnRequestModalVisibilityChange(false)}
                                isEasyPlusUser={userContext.isEasyPlusUser}
                                selectedDevices={selectedDevices}
            />
        </PageContainer>
    );
};

export default AlertSources;